
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Users, 
  ChefHat, 
  BarChart3, 
  Bell, 
  Upload, 
  Plus,
  UserCheck,
  UserX,
  Clock,
  BookOpen,
  TrendingUp
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalRecipes: 0,
    pendingRecipes: 0,
    totalTutorials: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);

      // Fetch user stats
      const { count: totalUsers } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });

      const { count: activeUsers } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .eq('subscription_status', 'active');

      // Fetch recipe stats
      const { count: totalRecipes } = await supabase
        .from('recipes')
        .select('*', { count: 'exact', head: true });

      const { count: pendingRecipes } = await supabase
        .from('pending_recipes')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');

      // Fetch tutorials stats
      const { count: totalTutorials } = await supabase
        .from('tutorials')
        .select('*', { count: 'exact', head: true });

      setStats({
        totalUsers: totalUsers || 0,
        activeUsers: activeUsers || 0,
        totalRecipes: totalRecipes || 0,
        pendingRecipes: pendingRecipes || 0,
        totalTutorials: totalTutorials || 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Error cargando estadísticas');
    } finally {
      setLoading(false);
    }
  };

  const navigationItems = [
    {
      title: 'Usuarios',
      description: 'Gestionar usuarios y suscripciones',
      icon: Users,
      href: '/admin/users',
      count: stats.totalUsers
    },
    {
      title: 'Recetas',
      description: 'Administrar recetas y contenido',
      icon: ChefHat,
      href: '/admin/recipes',
      count: stats.totalRecipes
    },
    {
      title: 'Tutoriales',
      description: 'Gestionar tutoriales y guías',
      icon: BookOpen,
      href: '/admin/tutorials',
      count: stats.totalTutorials
    },
    {
      title: 'Estadísticas',
      description: 'Análisis y métricas detalladas',
      icon: BarChart3,
      href: '/admin/statistics'
    },
    {
      title: 'Notificaciones',
      description: 'Enviar notificaciones a usuarios',
      icon: Bell,
      href: '/admin/notifications'
    },
    {
      title: 'Importar Recetas',
      description: 'Cargar recetas desde CSV',
      icon: Upload,
      href: '/admin/import-recipes'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Panel de Administración</h1>
          <p className="text-gray-600">Gestiona tu aplicación desde aquí</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Usuarios</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalUsers}</div>
              <div className="flex items-center mt-2">
                <UserCheck className="h-4 w-4 text-green-600 mr-1" />
                <span className="text-sm text-green-600">{stats.activeUsers} activos</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Recetas</CardTitle>
              <ChefHat className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalRecipes}</div>
              {stats.pendingRecipes > 0 && (
                <div className="flex items-center mt-2">
                  <Clock className="h-4 w-4 text-orange-600 mr-1" />
                  <span className="text-sm text-orange-600">{stats.pendingRecipes} pendientes</span>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Tutoriales</CardTitle>
              <BookOpen className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalTutorials}</div>
              <div className="flex items-center mt-2">
                <TrendingUp className="h-4 w-4 text-blue-600 mr-1" />
                <span className="text-sm text-blue-600">Contenido educativo</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Suscripciones</CardTitle>
              <UserCheck className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.activeUsers}</div>
              <div className="text-sm text-gray-600 mt-2">
                {((stats.activeUsers / stats.totalUsers) * 100).toFixed(1)}% conversión
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Navigation Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {navigationItems.map((item, index) => {
            const IconComponent = item.icon;
            return (
              <Card key={index} className="hover:shadow-lg transition-shadow cursor-pointer">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <IconComponent className="h-8 w-8 text-blue-600" />
                    {item.count !== undefined && (
                      <Badge variant="secondary">{item.count}</Badge>
                    )}
                  </div>
                  <CardTitle className="text-xl">{item.title}</CardTitle>
                  <CardDescription>{item.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button 
                    className="w-full" 
                    onClick={() => window.location.href = item.href}
                  >
                    Acceder
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="mt-8">
          <h2 className="text-2xl font-bold mb-4">Acciones Rápidas</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Button 
              className="h-20 flex flex-col gap-2"
              onClick={() => window.location.href = '/admin/recipe/new'}
            >
              <Plus className="h-6 w-6" />
              Nueva Receta
            </Button>
            <Button 
              variant="outline" 
              className="h-20 flex flex-col gap-2"
              onClick={() => window.location.href = '/admin/notifications'}
            >
              <Bell className="h-6 w-6" />
              Enviar Notificación
            </Button>
            <Button 
              variant="outline" 
              className="h-20 flex flex-col gap-2"
              onClick={() => window.location.href = '/admin/import-recipes'}
            >
              <Upload className="h-6 w-6" />
              Importar Recetas
            </Button>
            <Button 
              variant="outline" 
              className="h-20 flex flex-col gap-2"
              onClick={() => window.location.href = '/admin/tutorials'}
            >
              <BookOpen className="h-6 w-6" />
              Gestionar Tutoriales
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
